/**
 * ehr#777 - the header "Search clients" box filters the Contacts list as you type, without
 * Enter. Each keystroke restarts a short timer; when typing pauses the query is applied.
 * Enter applies at once (and cancels the pending timer), and clearing the box applies the
 * empty query at once so the full list comes back (#48).
 *
 * Framework-free so it can be unit tested with fake timers; App.tsx holds one in a ref.
 */

export const SEARCH_DELAY_MS = 250;

export interface SearchAsYouType {
  /** The box's text changed. */
  input: (value: string) => void;
  /** Apply now (Enter). */
  flush: (value: string) => void;
  /** Drop any pending apply (unmount). */
  cancel: () => void;
}

export function createSearchAsYouType(
  apply: (value: string) => void,
  delayMs: number = SEARCH_DELAY_MS,
): SearchAsYouType {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const flush = (value: string) => {
    cancel();
    apply(value);
  };
  const input = (value: string) => {
    if (value.trim() === '') {
      flush('');
      return;
    }
    cancel();
    timer = setTimeout(() => {
      timer = null;
      apply(value);
    }, delayMs);
  };
  return { input, flush, cancel };
}

/**
 * Tells whether an answer still belongs to the newest search. `next()` marks a new search
 * (the query changed) and returns its token; `peek()` returns the current token without
 * starting a new one (a quiet background refresh of the same search). `isCurrent(token)`
 * is false once a newer search has started, so an older answer that arrives late never
 * overwrites a newer one.
 */
export interface LatestGate {
  next: () => number;
  peek: () => number;
  isCurrent: (token: number) => boolean;
}

export function createLatestGate(): LatestGate {
  let latest = 0;
  return {
    next: () => ++latest,
    peek: () => latest,
    isCurrent: (token: number) => token === latest,
  };
}
